import { memo, useMemo } from 'react'
import type { MixerRowData, EdgeDragMode } from './audioRemixTypes'
import { WaveformBars, STATUS_INFO, formatTime } from '../shared/AudioRemixShared'
import { LEFT_RAIL_W } from './MixerRuler'

interface MixerTrackRowProps {
  row: MixerRowData
  muted: boolean
  solo: boolean
  edgeDragMode: EdgeDragMode
  playheadTime: number
  videoDuration: number
  origWaveformBars: number[]
  origSelStart: number | null
  origSelEnd: number | null
  onToggleMute: () => void
  onToggleSolo: () => void
  onSelectVersion: (trackId: string) => void
  onSoloPlay: (trackId: string) => void
  onBlockMouseDown: (e: React.MouseEvent, trackId: string, mode: 'move' | 'resize-left' | 'resize-right') => void
  onOrigMouseDown?: (e: React.MouseEvent<HTMLDivElement>) => void
  onAddCustomClip?: () => void
  hasOrigSelection?: boolean
}

export const MixerTrackRow = memo(function MixerTrackRow({
  row,
  muted,
  solo,
  edgeDragMode,
  playheadTime,
  videoDuration,
  origWaveformBars,
  origSelStart,
  origSelEnd,
  onToggleMute,
  onToggleSolo,
  onSelectVersion,
  onSoloPlay,
  onBlockMouseDown,
  onOrigMouseDown,
  onAddCustomClip,
  hasOrigSelection,
}: MixerTrackRowProps) {
  const char = row.characterColor
  const active = row.activeTrack
  const group = row.group

  const speedRatio = active.sourceDurationS && (active.endS - active.startS) > 0
    ? active.sourceDurationS / (active.endS - active.startS) : 1
  const showSpeed = Math.abs(speedRatio - 1) > 0.01
  const isPlaying = playheadTime >= active.startS && playheadTime <= active.endS && !muted

  const leftPct = videoDuration > 0 ? (active.startS / videoDuration) * 100 : 0
  const widthPct = videoDuration > 0 ? ((active.endS - active.startS) / videoDuration) * 100 : 0
  const trackDur = active.endS - active.startS

  const statusInfo = group.status && group.type !== 'voiceover' ? STATUS_INFO[group.status] : null

  const barCount = Math.max(10, Math.round(trackDur * 33))
  const waveformBars = useMemo(() => {
    if (row.kind === 'orig' && origWaveformBars.length > 0) return origWaveformBars
    return []
  }, [row.kind, origWaveformBars])

  return (
    <div
      className={`flex border-b border-border ${solo ? 'bg-amber-500/[0.04]' : ''}`}
      style={{ minHeight: 62 }}
    >
      {/* Left rail */}
      <div
        className={`flex-shrink-0 px-2.5 py-1.5 bg-surface-1 border-r border-border flex flex-col gap-1 ${muted ? 'opacity-45' : ''}`}
        style={{ width: LEFT_RAIL_W, borderLeft: `3px solid ${muted ? 'transparent' : char.fg}` }}
      >
        {/* Character label */}
        <div className="flex items-center gap-1.5">
          <span
            className={`w-2 h-2 rounded-full flex-shrink-0 ${isPlaying ? 'animate-pulse' : ''}`}
            style={{ background: char.wave, opacity: isPlaying ? 1 : 0.4 }}
          />
          <span className="text-[12px] font-semibold flex-1 truncate" style={{ color: char.fg }}>
            {row.kind === 'orig' ? 'Orig' : group.character}
          </span>
          {group.type === 'voiceover' && (
            <span className="px-1 py-0.5 rounded text-[9px] font-bold uppercase bg-purple-500/20 text-purple-400 border border-purple-500/30">
              VO
            </span>
          )}
          {statusInfo && (
            <span className={`px-1 py-0.5 rounded text-[9px] font-bold uppercase ${statusInfo.className}`} title={statusInfo.tooltip}>
              {group.status}
            </span>
          )}
        </div>

        {/* Emotion */}
        {group.emotion && (
          <span className="text-[10px] text-text-muted italic ml-3.5 -mt-0.5">({group.emotion})</span>
        )}

        {/* Controls: M S + time slot */}
        <div className="flex items-center gap-1">
          <button
            onClick={onToggleMute}
            className={`px-1 py-0.5 text-[9px] font-bold rounded transition-colors ${
              muted ? 'bg-red-500 text-white' : 'bg-surface-2 text-text-muted hover:text-foreground'
            }`}
            title="Mute (M)"
          >M</button>
          <button
            onClick={onToggleSolo}
            className={`px-1 py-0.5 text-[9px] font-bold rounded transition-colors ${
              solo ? 'bg-amber-500 text-[#1a0f09]' : 'bg-surface-2 text-text-muted hover:text-foreground'
            }`}
            title="Solo (S)"
          >S</button>
          <span className="text-[9px] font-mono tabular-nums text-text-muted/50 ml-auto">
            {formatTime(active.startS)}–{formatTime(active.endS)}
          </span>
        </div>

        {/* Version selector */}
        {row.versions.length > 1 && (
          <div className="flex items-center gap-1">
            <select
              className="flex-1 text-[10px] px-1 py-0.5 rounded bg-surface-2 border border-border text-foreground font-sans"
              value={active.id}
              onChange={(e) => onSelectVersion(e.target.value)}
            >
              {row.versions.map((v) => (
                <option key={v.id} value={v.id}>{v.label}</option>
              ))}
            </select>
            {showSpeed && (
              <span className="px-1 py-0.5 rounded text-[9px] font-bold font-mono bg-blue-500/[0.14] text-blue-300 border border-blue-500/30">
                {speedRatio.toFixed(2)}×
              </span>
            )}
          </div>
        )}
      </div>

      {/* Timeline lane */}
      <div
        className="flex-1 relative bg-surface-0 overflow-hidden"
        data-timeline
        onMouseDown={row.kind === 'orig' && onOrigMouseDown ? onOrigMouseDown : undefined}
        style={{ cursor: row.kind === 'orig' ? 'crosshair' : undefined }}
      >
        {/* Gridlines */}
        {Array.from({ length: Math.ceil(videoDuration) + 1 }).map((_, i) => (
          <div
            key={i}
            className={`absolute top-0 bottom-0 w-px ${i % 5 === 0 ? 'bg-white/[0.05]' : 'bg-white/[0.02]'}`}
            style={{ left: `${(i / videoDuration) * 100}%` }}
          />
        ))}

        {/* Active block */}
        <div
          className={`absolute top-1.5 bottom-1.5 rounded overflow-hidden ${muted ? 'opacity-40' : ''} ${row.kind === 'orig' ? 'pointer-events-none' : 'cursor-grab'}`}
          style={{
            left: `${leftPct}%`,
            width: `${widthPct}%`,
            background: muted ? 'rgba(255,255,255,0.04)' : char.bg,
            border: `1px solid ${muted ? 'rgba(255,255,255,0.06)' : char.border}`,
          }}
          data-testid="clip-block"
          onMouseDown={(e) => onBlockMouseDown(e, active.id, 'move')}
          onDoubleClick={() => onSoloPlay(active.id)}
        >
          {/* Label strip */}
          <div
            className="absolute top-0 left-0 right-0 h-3.5 px-1 flex items-center justify-between text-[8px] font-mono uppercase overflow-hidden whitespace-nowrap"
            style={{ background: 'rgba(0,0,0,0.25)', color: muted ? 'rgba(255,255,255,0.22)' : char.fg }}
          >
            <span className="truncate">{active.label} · {trackDur.toFixed(1)}s</span>
            {trackDur > 1.5 && row.kind !== 'orig' && (active.fadeInS > 0 || active.fadeOutS > 0) && (
              <span className="shrink-0 ml-1">fd {active.fadeInS.toFixed(1)}/{active.fadeOutS.toFixed(1)}</span>
            )}
          </div>

          {/* Waveform */}
          <div className="absolute top-3.5 left-0.5 right-0.5 bottom-0.5 overflow-hidden flex items-center">
            {row.kind === 'orig' ? (
              <WaveformBars bars={waveformBars} barCount={waveformBars.length || barCount} color={char.wave} opacity={0.9} />
            ) : (
              <WaveformBars bars={[]} barCount={barCount} color={char.wave} opacity={0.9} />
            )}
          </div>

          {/* Fade in overlay */}
          {active.fadeInS > 0 && (
            <div
              className="absolute top-3.5 bottom-0 left-0 pointer-events-none"
              style={{
                width: `${Math.min((active.fadeInS / trackDur) * 100, 50)}%`,
                background: 'linear-gradient(to right, rgba(245,158,11,0.18), transparent)',
                borderLeft: edgeDragMode === 'fade' ? '2px solid #f59e0b' : undefined,
              }}
            />
          )}

          {/* Fade out overlay */}
          {active.fadeOutS > 0 && (
            <div
              className="absolute top-3.5 bottom-0 right-0 pointer-events-none"
              style={{
                width: `${Math.min((active.fadeOutS / trackDur) * 100, 50)}%`,
                background: 'linear-gradient(to left, rgba(245,158,11,0.18), transparent)',
                borderRight: edgeDragMode === 'fade' ? '2px solid #f59e0b' : undefined,
              }}
            />
          )}

          {/* Resize handles */}
          <div
            className="absolute top-3.5 bottom-0 left-0 w-1 cursor-col-resize"
            style={{ background: edgeDragMode === 'fade' ? 'rgba(245,158,11,0.25)' : undefined }}
            onMouseDown={(e) => { e.stopPropagation(); onBlockMouseDown(e, active.id, 'resize-left') }}
          />
          <div
            className="absolute top-3.5 bottom-0 right-0 w-1 cursor-col-resize"
            style={{ background: edgeDragMode === 'fade' ? 'rgba(245,158,11,0.25)' : undefined }}
            onMouseDown={(e) => { e.stopPropagation(); onBlockMouseDown(e, active.id, 'resize-right') }}
          />

          {/* Speed badge */}
          {showSpeed && (
            <span
              className="absolute bottom-1 left-1 px-1 py-0.5 rounded text-[9px] font-bold font-mono bg-blue-500/25 border border-blue-500/40 text-blue-300 flex items-center gap-1"
              data-testid="speed-badge"
            >
              {speedRatio.toFixed(2)}×
            </span>
          )}
        </div>

        {/* Ghost versions (non-active tracks) */}
        {row.versions.filter((v) => v.id !== active.id).map((v) => {
          const gLeft = videoDuration > 0 ? (v.startS / videoDuration) * 100 : 0
          const gWidth = videoDuration > 0 ? ((v.endS - v.startS) / videoDuration) * 100 : 0
          return (
            <div
              key={v.id}
              className="absolute top-1.5 bottom-1.5 border border-dashed border-white/[0.12] rounded pointer-events-none"
              style={{ left: `${gLeft}%`, width: `${gWidth}%` }}
            />
          )
        })}

        {/* Orig selection overlay + floating Add button */}
        {row.kind === 'orig' && origSelStart != null && origSelEnd != null && videoDuration > 0 && (() => {
          const selLeft = (Math.min(origSelStart, origSelEnd) / videoDuration) * 100
          const selWidth = (Math.abs(origSelEnd - origSelStart) / videoDuration) * 100
          return (
            <>
              <div
                className="absolute top-0 bottom-0 bg-primary/20 border-x border-primary/40 pointer-events-none z-[1]"
                style={{ left: `${selLeft}%`, width: `${selWidth}%` }}
              />
              {hasOrigSelection && onAddCustomClip && (
                <button
                  onClick={(e) => { e.stopPropagation(); onAddCustomClip() }}
                  className="absolute z-[2] bottom-1 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-primary text-[#1a0f09] shadow-lg hover:bg-primary/90 transition-colors whitespace-nowrap"
                  style={{ left: `${selLeft + selWidth / 2}%`, transform: 'translateX(-50%)' }}
                >
                  + Add clip
                </button>
              )}
            </>
          )
        })()}

        {/* Playhead */}
        <div
          className="absolute top-0 bottom-0 w-px bg-red-500 pointer-events-none z-[1]"
          style={{ left: `${videoDuration > 0 ? (playheadTime / videoDuration) * 100 : 0}%` }}
        />
      </div>
    </div>
  )
})
